import { useState, ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import moment from 'moment';

import * as graphql from 'generated/graphql';

import useLocalize from '../../../kakapo-ui-application/hooks/useLocalize';
import { ParagraphSmall } from '../common/Paragraph';
import TooltipDark from '../tooltips/TooltipDark';
import PopoverTarget from '../popovers/PopoverTarget';

import CycleMomentInstructionModal from './CycleMomentInstructionModal';
import CycleMomentPopover from './CycleMomentPopover';
import CycleMomentSkipModal, { CycleMomentInstance } from './CycleMomentSkipModal';
import CycleMomentSendReminderModal from './CycleMomentSendReminderModal';
import { Spinner, DateIntervalNavigation, type ITimeMachine } from '@treams/treams-ui';
import { BellIcon, ConversationIcon, DialogueIcon, TargetIcon } from '@treams/treams-icons';
import { CycleMomentsContext } from 'settings/employees/Cycles';
import { nativeJs } from '@js-joda/core';
import { Interval } from '@js-joda/extra';

const CycleMomentsBoxHeader = styled(Box)`
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	background-color: ${(props) => props.theme.colors.white};
	border-radius: 4px 4px 0px 0px;
	border-bottom: none;
`;

const CycleMomentsBox = styled(Box)<{
	empty: boolean;
	mini?: boolean;
	allowPagination?: boolean;
	noBackground?: boolean;
}>`
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	background-color: ${(props) => (props.noBackground ? 'inherit' : props.theme.colors.white)};
	${(props) => (!props.allowPagination ? 'border-radius: 4px;' : 'border-radius: 0px 0px 4px 4px;')}
	box-shadow: 0 2px 4px ${(props) => rgba(props.theme.colors.lightBlue, 0.5)};
	position: relative;
	overflow: hidden;
	${(props) => props.allowPagination && 'border-top: none;'}

	${(props) =>
		props.mini &&
		`
		border: none;
		background: none;
		box-shadow: none;
	`}

	${(props) =>
		!props.empty &&
		!props.mini &&
		`
	&:before {
		content: '';
		width: 40px;
		display: block;
		position: absolute;
		z-index: 1;
		left: 0;
		top: 30px;
		bottom: 10px;
		background-image: linear-gradient(to left, rgba(255, 255, 255, 0.0), ${props.theme.colors.white});
	}
	&:after {
		content: '';
		width: 40px;
		display: block;
		position: absolute;
		z-index: 1;
		right: 0;
		top: 30px;
		bottom: 10px;
		background-image: linear-gradient(
			to right,
			rgba(255, 255, 255, 0.0),
			${props.theme.colors.white}
		);
	}
	`}
`;

const CycleMomentsTrack = styled.div`
	width: 100%;
	height: 2px;
	border-radius: 1px;
	position: relative;
	overflow: visible;
	background-color: ${(props) => props.theme.colors.lightBlue};
`;

const CycleMomentTrackCompleted = styled.div`
	width: calc(100% - 2px);
	height: 2px;
	position: absolute;
	top: 0;
	left: 0;
	background-color: ${(props) => props.theme.colors.green};
`;

const CycleMomentTrackDue = styled.div`
	width: calc(100% - 2px);
	height: 2px;
	position: absolute;
	z-index: 2;
	background-color: ${(props) => props.theme.colors.red};
`;

const CycleMomentTag = styled(Flex)<{
	status: graphql.MomentStatus;
	isDue: boolean;
	focus: boolean;
	$selected: boolean;
}>`
	padding: 0px 8px;
	border-radius: 4px;
	min-width: 40px;
	height: 30px;
	justify-content: center;
	position: relative;

	svg {
		display: block;
	}

	border: 1px solid transparent;

	// Color for selection, must override others when selected
	${(props) =>
		props.$selected &&
		css`
			background-color: rgba(76, 187, 23) !important;
		`}

	${(props) =>
		props.status === graphql.MomentStatus.Completed &&
		`
			color: ${props.theme.colors.white};
			background-color: ${props.theme.colors.green};
			border-color: ${props.theme.colors.green};
	`}

	${(props) =>
		props.status === graphql.MomentStatus.Open &&
		props.isDue &&
		`
			color: ${props.theme.colors.white};
			background-color: ${props.theme.colors.red};
			border-color: ${props.theme.colors.red};
	`}

	${(props) =>
		props.status === graphql.MomentStatus.Open &&
		!props.isDue &&
		`
			color: ${props.theme.colors.white};
			background-color: ${props.theme.colors.lightBlue};
			border-color: ${props.theme.colors.lightBlue};
	`}

	${(props) => props.focus && 'border: 1px solid rgba(0,0,0,0.1);'}
`;

const durationInBounds = (bounds: [Date, Date]): number => {
	return moment.utc(bounds[1]).diff(moment.utc(bounds[0]), 'days');
};

const CycleMomentsTrackToday = styled.div<{ label: string }>`
	background: ${(props) => props.theme.colors.primary};
	height: 10px;
	width: 1px;
	position: absolute;
	right: 0;
	top: -4px;

	&:before {
		content: '${(props) => props.label}';
		position: absolute;
		left: -50%;
		top: -18px;
		color: ${(props) => props.theme.colors.slateBlue};
		transform: translateX(-50%);
		z-index: 0;
	}
`;

const CycleMomentsTrackIndicator = styled.div<{ height: number }>`
	background-color: ${(props) => props.theme.colors.primary};
	height: ${(props) => props.height * 40 + 20}px;
	width: 1px;
	position: absolute;
	right: 0;
	top: 5px;
`;

const CycleMomentLine = styled.div`
	position: relative;

	&:before {
		content: '';
		position: absolute;
		width: 100%;
		height: 1px;
		top: 0;
		transform: translateY(-50%);
		border-top: 1px solid ${(props) => props.theme.colors.lightBlueLight};
	}
`;

const CycleMomentsTooltipTrackContainer = styled(Box)`
	position: absolute;
	top: 30px;
	left: 16px;
	right: 16px;
`;

const CycleMomentsTooltipTrack = ({
	bounds,
	children,
	mini,
}: {
	bounds: [Date, Date];
	children: ReactNode;
	mini?: boolean;
}) => {
	const [hoverOffsetX, setHoverOffsetX] = useState<number | undefined>(undefined);
	const [hoverProgress, setHoverProgress] = useState<number | undefined>(undefined);

	const daysDuration = durationInBounds(bounds);

	return (
		<Box
			// Create transparent hover track for easier access to the tooltip
			py={mini ? 0 : 4}
			my={mini ? 0 : -4}
			onMouseMove={(event) => {
				const { left } = event.currentTarget.getBoundingClientRect();
				const offsetX = event.clientX - left;
				setHoverOffsetX(offsetX);
				setHoverProgress(offsetX / event.currentTarget.getBoundingClientRect().width);
			}}
			onMouseLeave={() => {
				setHoverOffsetX(undefined);
			}}
		>
			{children}
			{hoverOffsetX !== undefined && !mini && (
				<div
					style={{
						width: '100px',
						height: '0',
						position: 'relative',
						left: `${hoverOffsetX}px`,
						transform: 'translateX(-50%) translateY(-10px)',
					}}
				>
					<TooltipDark direction="north" isHovered>
						{moment(bounds[0])
							.add(Math.round(hoverProgress! * daysDuration), 'days')
							.format('DD MMMM YYYY')}
					</TooltipDark>
				</div>
			)}
		</Box>
	);
};

const CycleMomentOffset = ({
	bounds,
	startDate,
	endDate,
	children,
}: {
	startDate: Date;
	endDate: Date;
	children: ReactNode;
	bounds: [Date, Date];
}) => {
	const daysInRange = moment.utc(bounds[1]).diff(moment.utc(bounds[0]), 'days');

	const startDateOffset = moment.utc(startDate).diff(moment(bounds[0]), 'days');
	const daysDuration = durationInBounds([startDate, endDate]);

	const left = startDateOffset / daysInRange;
	const width = Math.min(daysDuration / daysInRange, 1);

	return (
		<div
			style={{
				position: 'relative',
				left: `${left * 100}%`,
				width: `${width * 100}%`,
				minWidth: 40,
			}}
		>
			{children}
		</div>
	);
};

interface IMomentRepresentable {
	startDate: Date;
	endDate: Date;
	title: string;
	cycle: graphql.CycleTemplate;
	cycleInstanceID: string;
	template: graphql.MomentTemplate;
	type: graphql.MomentType;
	instance?: graphql.MomentInstance;
	status: graphql.MomentStatus;
}

interface IWithIsDue {
	isDue: boolean;
}

function CycleMoment({
	bounds,
	momentInstance,
	canSkip,
	canSendReminder,
}: {
	bounds: [Date, Date];
	momentInstance: IMomentRepresentable & IWithIsDue;
	canSkip: boolean;
	canSendReminder: boolean;
}) {
	const [showSkipModal, setShowSkipModal] = useState(false);
	const [showReminderModal, setShowReminderModal] = useState(false);
	const { setSelectedMoment } = useContext(CycleMomentsContext);

	const [instructionModal, setInstructionModal] = useState(
		undefined as
			| {
					title: string;
					instruction?: graphql.LocalizedAttributedString;
					buttonTitle: string;
					callback: () => void;
			  }
			| undefined,
	);

	const moment: CycleMomentInstance = {
		cycleInstanceID: momentInstance.cycleInstanceID,
		instance: momentInstance.instance,
		template: momentInstance.template,
	};

	const handleSetSelection = () => {
		setSelectedMoment(moment);
	};

	return (
		<Box onClick={() => handleSetSelection()}>
			<CycleMomentOffset
				bounds={bounds}
				startDate={momentInstance.startDate}
				endDate={momentInstance.endDate}
			>
				<PopoverTarget
					content={
						<CycleMomentPopover
							cycle={momentInstance.cycle}
							template={momentInstance.template}
							instance={momentInstance.instance}
							startDate={momentInstance.startDate}
							endDate={momentInstance.endDate}
							onSkip={canSkip ? () => setShowSkipModal(true) : undefined}
							onSendReminder={canSendReminder ? () => setShowReminderModal(true) : undefined}
							showInstruction={(options) => setInstructionModal(options)}
						/>
					}
				>
					<div style={{ height: 38 }} />
				</PopoverTarget>
			</CycleMomentOffset>
			{showSkipModal && (
				<CycleMomentSkipModal
					moments={[{ ...momentInstance }]}
					onDismiss={() => setShowSkipModal(false)}
				/>
			)}
			{showReminderModal && momentInstance.instance && (
				<CycleMomentSendReminderModal
					instance={momentInstance.instance}
					onDismiss={() => setShowReminderModal(false)}
				/>
			)}
			{instructionModal && (
				<CycleMomentInstructionModal
					title={instructionModal.title}
					instruction={instructionModal.instruction}
					buttonTitle={instructionModal.buttonTitle}
					onCancel={() => setInstructionModal(undefined)}
					onStart={() => {
						instructionModal.callback();
					}}
				/>
			)}
		</Box>
	);
}

function CycleMomentsPreviewPopovers({
	bounds,
	momentsWithDue,
	canSkip,
	canSendReminder,
}: {
	bounds: [Date, Date];
	momentsWithDue: (IMomentRepresentable & IWithIsDue)[];
	canSkip: boolean;
	canSendReminder: boolean;
}) {
	return (
		<Box
			pb={4}
			px={3}
			mt={4}
			style={{
				position: 'absolute',
				width: '100%',
				left: 0,
				top: 0,
			}}
		>
			{momentsWithDue.map((momentInstance, i) => (
				<CycleMoment
					key={i}
					bounds={bounds}
					momentInstance={momentInstance}
					canSkip={canSkip}
					canSendReminder={canSendReminder}
				/>
			))}
		</Box>
	);
}

const Icon = styled(Box)<{ color: string }>`
	svg path {
		fill: ${(props) => props.theme.colors[props.color]};
	}
`;

export default function CycleMomentsPreviewView({
	bounds,
	firstMomentDue,
	currentDate,
	momentsWithDue,
	mini,
	canSkip,
	canSendReminder,
	loading,
	allowPagination,
	timeMachineValue,
	onChangeTimeMachine,
}: {
	bounds: [Date, Date];
	firstMomentDue?: Date;
	currentDate: Date;
	momentsWithDue: (IMomentRepresentable & IWithIsDue)[];
	mini: boolean;
	canSkip: boolean;
	canSendReminder: boolean;
	loading?: boolean;
	allowPagination?: boolean;
	timeMachineValue?: ITimeMachine;
	onChangeTimeMachine?: (value: ITimeMachine) => void;
	selected?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	/* Convert Native Date to Joda Instant */
	const today = nativeJs(currentDate).toInstant();

	/* Bounds are string dates, convert to JS Date first */
	const [lowerBound, upperBound] = bounds.map((bound) => nativeJs(new Date(bound)).toInstant());

	const todayInRange = Interval.of(lowerBound, upperBound).contains(today);
	const { selectedMoments } = useContext(CycleMomentsContext);

	return (
		<>
			{!mini && allowPagination && (
				<CycleMomentsBoxHeader>
					<Flex
						justifyContent="space-between"
						alignItems="center"
						overflowX={['scroll', 'inherit', 'inherit', 'inherit']}
						p={3}
						pb={0}
					>
						<Box>
							<ParagraphSmall color="slateBlue">
								{moment(bounds[0]).format('LL')} &mdash; {moment(bounds[1]).format('LL')}
							</ParagraphSmall>
						</Box>
						<Flex justifyContent="flex-end" flexShrink={0}>
							{timeMachineValue && onChangeTimeMachine && (
								<DateIntervalNavigation
									value={timeMachineValue}
									onChange={onChangeTimeMachine}
									disableQuery
									allowFutureDates
								/>
							)}
						</Flex>
					</Flex>
				</CycleMomentsBoxHeader>
			)}

			<div style={{ position: 'relative' }}>
				<CycleMomentsBox
					mini={mini}
					allowPagination={allowPagination}
					py={4}
					px={3}
					empty={momentsWithDue.length === 0}
				>
					{!mini && (
						<div>
							{!loading &&
								momentsWithDue.map((momentInstance, i) => {
									const isSelected = selectedMoments.some(
										(moment) => moment.instance?.id === momentInstance.instance?.id,
									);

									return (
										<Box key={momentInstance.instance?.id ?? i}>
											<CycleMomentLine>
												<Box py={1}>
													<CycleMomentOffset
														bounds={bounds}
														startDate={momentInstance.startDate}
														endDate={momentInstance.endDate}
													>
														<Box>
															<div style={{ position: 'relative', minWidth: '40px' }}>
																<CycleMomentTag
																	$selected={isSelected}
																	isDue={momentInstance.isDue}
																	status={momentInstance.status}
																	alignItems="center"
																	focus
																>
																	{momentInstance.type === graphql.MomentType.Evaluation && (
																		<Icon
																			size={22}
																			color={
																				momentInstance.isDue ||
																				momentInstance.status === graphql.MomentStatus.Completed
																					? 'white'
																					: 'slateBlue'
																			}
																		>
																			<ConversationIcon />
																		</Icon>
																	)}
																	{momentInstance.type === graphql.MomentType.Feedback && (
																		<Icon
																			size={22}
																			color={
																				momentInstance.isDue ||
																				momentInstance.status === graphql.MomentStatus.Completed
																					? 'white'
																					: 'slateBlue'
																			}
																		>
																			<DialogueIcon />
																		</Icon>
																	)}
																	{momentInstance.type === graphql.MomentType.Goals && (
																		<Icon
																			size={22}
																			color={
																				momentInstance.isDue ||
																				momentInstance.status === graphql.MomentStatus.Completed
																					? 'white'
																					: 'slateBlue'
																			}
																		>
																			<TargetIcon />
																		</Icon>
																	)}
																	{momentInstance.type === graphql.MomentType.Message && (
																		<Icon
																			size={22}
																			color={
																				momentInstance.isDue ||
																				momentInstance.status === graphql.MomentStatus.Completed
																					? 'white'
																					: 'slateBlue'
																			}
																		>
																			<BellIcon />
																		</Icon>
																	)}
																	<div
																		style={{
																			position: 'absolute',
																			left: '100%',
																			top: '50%',
																			transform: 'translateY(-50%)',
																		}}
																	>
																		<Flex ml={2} alignItems="center">
																			<ParagraphSmall
																				color="slateBlue"
																				style={{ whiteSpace: 'nowrap' }}
																			>
																				{localize(momentInstance.template.title).text}
																			</ParagraphSmall>
																		</Flex>
																	</div>
																</CycleMomentTag>
															</div>
														</Box>
													</CycleMomentOffset>
												</Box>
											</CycleMomentLine>
										</Box>
									);
								})}
							<CycleMomentLine />

							{loading && (
								<Flex pt={3} justifyContent="center">
									<Spinner />
								</Flex>
							)}

							{!loading && momentsWithDue.length === 0 && (
								<Box pt={3} mb={-3}>
									<ParagraphSmall color="slateBlue">
										{t('cycles.moment.preview.none')}
									</ParagraphSmall>
								</Box>
							)}
						</div>
					)}

					<CycleMomentsTooltipTrackContainer mb={mini ? 0 : 3}>
						<CycleMomentsTooltipTrack bounds={bounds} mini={mini}>
							<CycleMomentsTrack>
								<CycleMomentOffset bounds={bounds} startDate={bounds[0]} endDate={currentDate}>
									{firstMomentDue && <CycleMomentTrackDue />}

									{!mini && todayInRange && (
										<>
											<CycleMomentsTrackToday label={t('common.today')} />

											{momentsWithDue.length > 0 && (
												<CycleMomentsTrackIndicator height={momentsWithDue.length} />
											)}
										</>
									)}
								</CycleMomentOffset>

								{todayInRange && (
									<CycleMomentOffset bounds={bounds} startDate={bounds[0]} endDate={currentDate}>
										<CycleMomentTrackCompleted />
									</CycleMomentOffset>
								)}
							</CycleMomentsTrack>
						</CycleMomentsTooltipTrack>
					</CycleMomentsTooltipTrackContainer>
				</CycleMomentsBox>

				{!mini && (
					<CycleMomentsPreviewPopovers
						bounds={bounds}
						momentsWithDue={momentsWithDue}
						canSkip={canSkip}
						canSendReminder={canSendReminder}
					/>
				)}
			</div>
		</>
	);
}
